import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

function Home() {
  // Dashboard cards with icons and descriptions
  const dashboards = [
    {
      title: 'Demographics',
      description: 'Membership demographics and risk scores',
      icon: '👥',
      path: '/demographics',
      category: 'Patient'
    },
    {
      title: 'Chronic Conditions',
      description: 'Comorbidities and conditions analysis',
      icon: '⚕️',
      path: '/chronic-conditions',
      category: 'Patient'
    },
    {
      title: 'SNF Dashboard',
      description: 'Skilled nursing facility utilization',
      icon: '🏥',
      path: '/snf',
      category: 'Cost & Utilization'
    },
    {
      title: 'INP Dashboard',
      description: 'Inpatient admissions and readmissions',
      icon: '🛏️',
      path: '/inp',
      category: 'Cost & Utilization'
    },
    {
      title: 'HHA Dashboard',
      description: 'Home health agency services',
      icon: '🏠',
      path: '/hha',
      category: 'Cost & Utilization'
    },
    {
      title: 'HOS Dashboard',
      description: 'Hospice care services',
      icon: '☮️',
      path: '/hos',
      category: 'Cost & Utilization'
    },
    {
      title: 'OP Dashboard',
      description: 'Outpatient facility services',
      icon: '🏢',
      path: '/op',
      category: 'Cost & Utilization'
    },
    {
      title: 'PHY Dashboard',
      description: 'Physician and professional services',
      icon: '👨‍⚕️',
      path: '/phy',
      category: 'Cost & Utilization'
    },
    {
      title: 'DME Dashboard',
      description: 'Durable medical equipment',
      icon: '🚑',
      path: '/dme',
      category: 'Cost & Utilization'
    },
    {
      title: 'ER Dashboard',
      description: 'Emergency department utilization',
      icon: '🚨',
      path: '/er',
      category: 'Cost & Utilization'
    },
    {
      title: 'TCOC Dashboard',
      description: 'Total cost of care and PMPY analysis',
      icon: '💰',
      path: '/tcoc',
      category: 'Financial'
    },
  ];

  // Capability cards - informational only
  const capabilities = [
    {
      icon: '📊',
      title: 'Population Analytics',
      description: 'Attributed population view with demographics, risk scores, and chronic conditions',
      outcome: 'Identify which patients and conditions are driving cost and utilization'
    },
    {
      icon: '🚑',
      title: 'Emergency Department Management',
      description: 'ED utilization tracking and avoidable visit identification',
      outcome: 'Reduce preventable emergency visits and manage acute care costs'
    },
    {
      icon: '🏥',
      title: 'Inpatient Performance',
      description: 'Readmissions analysis and inpatient length-of-stay tracking',
      outcome: 'Control inpatient utilization and reduce preventable readmissions'
    },
    {
      icon: '🏨',
      title: 'Post-Acute Analytics',
      description: 'SNF, HHA, and Hospice utilization and cost insights',
      outcome: 'Control and manage post-acute care utilization and cost'
    },
    {
      icon: '🤝',
      title: 'Care Services Intelligence',
      description: 'Visibility into AWVs, ACP, Welcome to Medicare (IPPE), and Depression Screening activity',
      outcome: 'Provide better, proactive care while reducing avoidable utilization and cost'
    },
    {
      icon: '📈',
      title: 'MSSP Actuarial Projections',
      description: 'CMS-aligned methodology with MSSP monthly projections',
      outcome: 'No end-of-year surprises — understand savings or losses as the year unfolds'
    }
  ];

  return (
    <div className="home-container">
      {/* Dashboards Section */}
      <div className="dashboards-section">
        <h2 className="section-header">📈 Dashboards</h2>
        <p className="section-subtitle">Access healthcare analytics and quality reporting tools</p>

        {/* Patient Analytics */}
        <div className="category-section">
          <h3 className="category-title">Patient Analytics</h3>
          <div className="dashboards-grid">
            {dashboards.filter(d => d.category === 'Patient').map((dashboard, index) => (
              <Link key={index} to={dashboard.path} className="dashboard-card-link">
                <div className="dashboard-card">
                  <div className="card-header-row">
                    <h3>{dashboard.title}</h3>
                    <span className="dashboard-icon">{dashboard.icon}</span>
                  </div>
                  <p className="dashboard-description">{dashboard.description}</p>
                  <div className="card-footer">
                    <span className="dashboard-category">{dashboard.category}</span>
                    <span className="arrow-icon">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Cost & Utilization */}
        <div className="category-section">
          <h3 className="category-title">Cost & Utilization</h3>
          <div className="dashboards-grid">
            {dashboards.filter(d => d.category === 'Cost & Utilization').map((dashboard, index) => (
              <Link key={index} to={dashboard.path} className="dashboard-card-link">
                <div className="dashboard-card">
                  <div className="card-header-row">
                    <h3>{dashboard.title}</h3>
                    <span className="dashboard-icon">{dashboard.icon}</span>
                  </div>
                  <p className="dashboard-description">{dashboard.description}</p>
                  <div className="card-footer">
                    <span className="dashboard-category">{dashboard.category}</span>
                    <span className="arrow-icon">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Financial & Quality */}
        <div className="category-section">
          <h3 className="category-title">Financial & Quality</h3>
          <div className="dashboards-grid">
            {dashboards.filter(d => d.category === 'Financial' || d.category === 'Quality').map((dashboard, index) => (
              <Link key={index} to={dashboard.path} className="dashboard-card-link">
                <div className="dashboard-card">
                  <div className="card-header-row">
                    <h3>{dashboard.title}</h3>
                    <span className="dashboard-icon">{dashboard.icon}</span>
                  </div>
                  <p className="dashboard-description">{dashboard.description}</p>
                  <div className="card-footer">
                    <span className="dashboard-category">{dashboard.category}</span>
                    <span className="arrow-icon">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Platform Capabilities Section */}
      <div className="capabilities-section">
        <h2 className="section-header">⭐ Platform Capabilities</h2>
        <p className="section-subtitle">Key features and benefits driving better outcomes and cost savings</p>

        <div className="capabilities-grid">
          {capabilities.map((capability, index) => (
            <div key={index} className="capability-card">
              <span className="capability-icon">{capability.icon}</span>
              <h3>{capability.title}</h3>
              <p className="capability-description">{capability.description}</p>
              <p className="capability-outcome"><strong>Outcome:</strong> {capability.outcome}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Home;

